/**
 * Centralized Permission Configuration
 * Single source of truth for all role-based access control
 * 
 * Structure:
 * - Page-level access
 * - Field-level visibility
 * - Action permissions (create, edit, delete, view)
 * - Data sensitivity levels
 */

// Define all departments and access levels
export const DEPARTMENTS = {
  MANAGEMENT: 'Management',
  SALES: 'Sales',
  ESTIMATION: 'Estimation',
  PROJECTS: 'Projects',
  ACCOUNTS: 'Accounts and Finance',
  HR: 'HR'
};

export const ACCESS_LEVELS = {
  ADMIN: 'Admin',
  MANAGER: 'Manager',
  TEAM_LEAD: 'Team Lead',
  TEAM_MEMBER: 'Team Member'
};

// Define sensitivity levels for data
export const SENSITIVITY = {
  PUBLIC: 'public',           // Everyone can see
  INTERNAL: 'internal',       // All logged-in users
  CONFIDENTIAL: 'confidential', // Management + specific roles
  RESTRICTED: 'restricted'    // Only specific roles
};

/**
 * PAGE ACCESS MATRIX
 * Define which roles can access which pages
 */
export const PAGE_ACCESS = {
  '/dashboard': {
    allowed: [
      { department: DEPARTMENTS.MANAGEMENT, level: '*' },
      { department: DEPARTMENTS.PROJECTS, level: '*' },
      { department: DEPARTMENTS.ESTIMATION, level: [ACCESS_LEVELS.MANAGER, ACCESS_LEVELS.ADMIN] }
    ],
    redirect: {
      [DEPARTMENTS.SALES]: '/sales',
      [DEPARTMENTS.ESTIMATION]: { [ACCESS_LEVELS.TEAM_MEMBER]: '/estimations' }
    }
  },
  
  '/projects': {
    allowed: [
      { department: DEPARTMENTS.MANAGEMENT, level: '*' },
      { department: DEPARTMENTS.PROJECTS, level: '*' },
      { department: DEPARTMENTS.ACCOUNTS, level: [ACCESS_LEVELS.MANAGER, ACCESS_LEVELS.ADMIN] }
    ],
    denied: [
      { department: DEPARTMENTS.SALES, level: '*' },
      { department: DEPARTMENTS.ESTIMATION, level: '*' }
    ]
  },
  
  '/clients': {
    allowed: 'all',
    restrictions: {
      [DEPARTMENTS.SALES]: {
        [ACCESS_LEVELS.TEAM_MEMBER]: { dataScope: 'assigned_only' },
        [ACCESS_LEVELS.TEAM_LEAD]: { dataScope: 'team_only' }
      }
    }
  },
  
  '/estimations': {
    allowed: 'all',
    restrictions: {
      [DEPARTMENTS.SALES]: {
        [ACCESS_LEVELS.TEAM_MEMBER]: { dataScope: 'assigned_only', readOnly: true },
        [ACCESS_LEVELS.TEAM_LEAD]: { dataScope: 'team_only', readOnly: true }
      },
      [DEPARTMENTS.ESTIMATION]: {
        [ACCESS_LEVELS.TEAM_MEMBER]: { readOnly: false, hiddenFields: ['proposal_link'] }
      }
    }
  },
  
  '/invoices': {
    allowed: [
      { department: DEPARTMENTS.MANAGEMENT, level: '*' },
      { department: DEPARTMENTS.ACCOUNTS, level: '*' },
      { department: DEPARTMENTS.PROJECTS, level: [ACCESS_LEVELS.MANAGER, ACCESS_LEVELS.ADMIN] }
    ]
  },
  
  '/sales': {
    allowed: [
      { department: DEPARTMENTS.MANAGEMENT, level: '*' },
      { department: DEPARTMENTS.SALES, level: '*' }
    ]
  },
  
  '/reports': {
    allowed: [
      { department: DEPARTMENTS.MANAGEMENT, level: '*' },
      { department: '*', level: [ACCESS_LEVELS.MANAGER, ACCESS_LEVELS.ADMIN] }
    ]
  },
  
  '/users': {
    allowed: [
      { department: DEPARTMENTS.MANAGEMENT, level: '*' },
      { department: DEPARTMENTS.HR, level: [ACCESS_LEVELS.MANAGER, ACCESS_LEVELS.ADMIN] }
    ]
  },
  
  '/employees': {
    allowed: [
      { department: DEPARTMENTS.MANAGEMENT, level: '*' },
      { department: DEPARTMENTS.HR, level: '*' }
    ]
  },
  
  '/teams': {
    allowed: [
      { department: DEPARTMENTS.MANAGEMENT, level: '*' },
      { department: DEPARTMENTS.HR, level: [ACCESS_LEVELS.MANAGER, ACCESS_LEVELS.ADMIN] }
    ]
  },

  '/leads': {
    allowed: [
      { department: DEPARTMENTS.MANAGEMENT, level: '*' },
      { department: DEPARTMENTS.SALES, level: '*' }
    ]
  },

  '/lead-approvals': {
    allowed: [
      { department: DEPARTMENTS.MANAGEMENT, level: [ACCESS_LEVELS.MANAGER, ACCESS_LEVELS.ADMIN] }
    ]
  },

  '/interns': {
    allowed: [
      { department: DEPARTMENTS.MANAGEMENT, level: '*' },
      { department: DEPARTMENTS.HR, level: '*' }
    ]
  },

  '/timesheets': {
    allowed: [
      { department: DEPARTMENTS.MANAGEMENT, level: '*' },
      { department: DEPARTMENTS.PROJECTS, level: '*' },
      { department: DEPARTMENTS.ESTIMATION, level: '*' }
    ]
  },

  '/vendors': {
    allowed: [
      { department: DEPARTMENTS.MANAGEMENT, level: '*' },
      { department: DEPARTMENTS.ACCOUNTS, level: '*' },
      { department: DEPARTMENTS.PROJECTS, level: [ACCESS_LEVELS.MANAGER, ACCESS_LEVELS.ADMIN] }
    ]
  },

  '/company-settings': {
    allowed: [
      { department: DEPARTMENTS.MANAGEMENT, level: [ACCESS_LEVELS.ADMIN] }
    ]
  },

  '/permissions': {
    allowed: [
      { department: DEPARTMENTS.MANAGEMENT, level: [ACCESS_LEVELS.ADMIN] }
    ]
  },

  '/error-settings': {
    allowed: [
      { department: DEPARTMENTS.MANAGEMENT, level: '*' },
      { department: DEPARTMENTS.PROJECTS, level: [ACCESS_LEVELS.MANAGER, ACCESS_LEVELS.ADMIN] }
    ]
  },

  '/error-tracking': {
    allowed: [
      { department: DEPARTMENTS.MANAGEMENT, level: '*' },
      { department: DEPARTMENTS.PROJECTS, level: '*' }
    ]
  },

  '/webhook-settings': {
    allowed: [
      { department: DEPARTMENTS.MANAGEMENT, level: '*' }
    ]
  },

  '/leave-management': {
    allowed: 'all'
  },

  '/leave-approvals': {
    allowed: [
      { department: DEPARTMENTS.MANAGEMENT, level: [ACCESS_LEVELS.MANAGER, ACCESS_LEVELS.ADMIN, ACCESS_LEVELS.TEAM_LEAD] },
      { department: DEPARTMENTS.SALES, level: [ACCESS_LEVELS.MANAGER, ACCESS_LEVELS.ADMIN, ACCESS_LEVELS.TEAM_LEAD] },
      { department: DEPARTMENTS.PROJECTS, level: [ACCESS_LEVELS.MANAGER, ACCESS_LEVELS.ADMIN, ACCESS_LEVELS.TEAM_LEAD] },
      { department: DEPARTMENTS.HR, level: [ACCESS_LEVELS.MANAGER, ACCESS_LEVELS.ADMIN] }
    ]
  },

  '/hr/leave-allocations': {
    allowed: [
      { department: DEPARTMENTS.MANAGEMENT, level: [ACCESS_LEVELS.ADMIN] },
      { department: DEPARTMENTS.HR, level: [ACCESS_LEVELS.MANAGER, ACCESS_LEVELS.ADMIN] }
    ]
  },

  '/hr/holidays': {
    allowed: [
      { department: DEPARTMENTS.MANAGEMENT, level: [ACCESS_LEVELS.ADMIN] },
      { department: DEPARTMENTS.HR, level: [ACCESS_LEVELS.MANAGER, ACCESS_LEVELS.ADMIN] }
    ]
  },

  // Performance & Awards Module
  '/performance/kra-settings': {
    allowed: [
      { department: DEPARTMENTS.MANAGEMENT, level: '*' },
      { department: DEPARTMENTS.HR, level: [ACCESS_LEVELS.MANAGER, ACCESS_LEVELS.ADMIN] }
    ]
  },

  '/performance/employee-targets': {
    allowed: [
      { department: DEPARTMENTS.MANAGEMENT, level: '*' },
      { department: DEPARTMENTS.HR, level: [ACCESS_LEVELS.MANAGER, ACCESS_LEVELS.ADMIN] }
    ]
  },

  '/performance/summary': {
    allowed: [
      { department: DEPARTMENTS.MANAGEMENT, level: '*' },
      { department: DEPARTMENTS.HR, level: '*' },
      { department: '*', level: [ACCESS_LEVELS.MANAGER, ACCESS_LEVELS.ADMIN] }
    ]
  },

  '/performance/awards': {
    allowed: [
      { department: DEPARTMENTS.MANAGEMENT, level: '*' },
      { department: DEPARTMENTS.HR, level: '*' },
      { department: '*', level: [ACCESS_LEVELS.MANAGER, ACCESS_LEVELS.ADMIN, ACCESS_LEVELS.TEAM_LEAD] }
    ]
  },

  '/performance/reports': {
    allowed: [
      { department: DEPARTMENTS.MANAGEMENT, level: '*' },
      { department: DEPARTMENTS.HR, level: [ACCESS_LEVELS.MANAGER, ACCESS_LEVELS.ADMIN] }
    ]
  },

  '/performance/my-performance': {
    allowed: 'all'
  }
};

/**
 * FIELD-LEVEL VISIBILITY MATRIX
 * Define which fields are visible to which roles
 * sensitivity: public, internal, confidential, restricted
 */
export const FIELD_PERMISSIONS = {
  // Project fields
  projects: {
    project_number: { sensitivity: SENSITIVITY.PUBLIC },
    project_name: { sensitivity: SENSITIVITY.PUBLIC },
    client_name: { sensitivity: SENSITIVITY.PUBLIC },
    status: { sensitivity: SENSITIVITY.INTERNAL },
    
    // Cost fields - only specific roles
    base_price: { 
      sensitivity: SENSITIVITY.CONFIDENTIAL,
      allowedRoles: [
        { department: DEPARTMENTS.MANAGEMENT, level: '*' },
        { department: DEPARTMENTS.SALES, level: '*' },
        { department: DEPARTMENTS.ACCOUNTS, level: '*' },
        { department: DEPARTMENTS.PROJECTS, level: [ACCESS_LEVELS.MANAGER, ACCESS_LEVELS.ADMIN] }
      ]
    },
    detailing_cost: { 
      sensitivity: SENSITIVITY.CONFIDENTIAL,
      allowedRoles: [
        { department: DEPARTMENTS.MANAGEMENT, level: '*' },
        { department: DEPARTMENTS.SALES, level: '*' },
        { department: DEPARTMENTS.ACCOUNTS, level: '*' }
      ]
    },
    engineering_cost: { 
      sensitivity: SENSITIVITY.CONFIDENTIAL,
      allowedRoles: [
        { department: DEPARTMENTS.MANAGEMENT, level: '*' },
        { department: DEPARTMENTS.SALES, level: '*' },
        { department: DEPARTMENTS.ACCOUNTS, level: '*' }
      ]
    },
    
    // Internal management fields
    profit_margin: {
      sensitivity: SENSITIVITY.RESTRICTED,
      allowedRoles: [
        { department: DEPARTMENTS.MANAGEMENT, level: '*' },
        { department: DEPARTMENTS.ACCOUNTS, level: [ACCESS_LEVELS.MANAGER, ACCESS_LEVELS.ADMIN] }
      ]
    },
    
    // Dates and schedules
    start_date: { sensitivity: SENSITIVITY.INTERNAL },
    end_date: { sensitivity: SENSITIVITY.INTERNAL },
    estimated_hours: { sensitivity: SENSITIVITY.INTERNAL },
    allocated_hours: { sensitivity: SENSITIVITY.INTERNAL }
  },
  
  // Client fields
  clients: {
    client_name: { sensitivity: SENSITIVITY.PUBLIC },
    client_number: { sensitivity: SENSITIVITY.PUBLIC },
    industry: { sensitivity: SENSITIVITY.INTERNAL },
    
    // Billing information - restricted
    billing_address: { 
      sensitivity: SENSITIVITY.CONFIDENTIAL,
      allowedRoles: [
        { department: DEPARTMENTS.MANAGEMENT, level: '*' },
        { department: DEPARTMENTS.ACCOUNTS, level: '*' },
        { department: DEPARTMENTS.SALES, level: [ACCESS_LEVELS.MANAGER, ACCESS_LEVELS.ADMIN, ACCESS_LEVELS.TEAM_LEAD] }
      ]
    },
    billing_approval: {
      sensitivity: SENSITIVITY.CONFIDENTIAL,
      allowedRoles: [
        { department: DEPARTMENTS.MANAGEMENT, level: '*' },
        { department: DEPARTMENTS.ACCOUNTS, level: '*' }
      ]
    },
    ccn_hourly_rate: {
      sensitivity: SENSITIVITY.RESTRICTED,
      allowedRoles: [
        { department: DEPARTMENTS.MANAGEMENT, level: '*' },
        { department: DEPARTMENTS.ACCOUNTS, level: [ACCESS_LEVELS.MANAGER, ACCESS_LEVELS.ADMIN] }
      ]
    }
  },
  
  // Estimation fields
  estimations: {
    estimation_number: { sensitivity: SENSITIVITY.PUBLIC },
    project_name: { sensitivity: SENSITIVITY.PUBLIC },
    client_name: { sensitivity: SENSITIVITY.PUBLIC },
    
    detailing_cost: { 
      sensitivity: SENSITIVITY.CONFIDENTIAL,
      allowedRoles: [
        { department: DEPARTMENTS.MANAGEMENT, level: '*' },
        { department: DEPARTMENTS.SALES, level: '*' },
        { department: DEPARTMENTS.ACCOUNTS, level: '*' }
      ]
    },
    engineering_cost: { 
      sensitivity: SENSITIVITY.CONFIDENTIAL,
      allowedRoles: [
        { department: DEPARTMENTS.MANAGEMENT, level: '*' },
        { department: DEPARTMENTS.SALES, level: '*' },
        { department: DEPARTMENTS.ACCOUNTS, level: '*' }
      ]
    },
    
    proposal_link: {
      sensitivity: SENSITIVITY.CONFIDENTIAL,
      allowedRoles: [
        { department: DEPARTMENTS.MANAGEMENT, level: '*' },
        { department: DEPARTMENTS.SALES, level: '*' }
      ]
    }
  },
  
  // Invoice fields
  invoices: {
    invoice_number: { sensitivity: SENSITIVITY.INTERNAL },
    amount: { 
      sensitivity: SENSITIVITY.CONFIDENTIAL,
      allowedRoles: [
        { department: DEPARTMENTS.MANAGEMENT, level: '*' },
        { department: DEPARTMENTS.ACCOUNTS, level: '*' },
        { department: DEPARTMENTS.PROJECTS, level: [ACCESS_LEVELS.MANAGER, ACCESS_LEVELS.ADMIN] }
      ]
    },
    payment_status: { 
      sensitivity: SENSITIVITY.CONFIDENTIAL,
      allowedRoles: [
        { department: DEPARTMENTS.MANAGEMENT, level: '*' },
        { department: DEPARTMENTS.ACCOUNTS, level: '*' }
      ]
    },
    payment_entries: {
      sensitivity: SENSITIVITY.RESTRICTED,
      allowedRoles: [
        { department: DEPARTMENTS.MANAGEMENT, level: '*' },
        { department: DEPARTMENTS.ACCOUNTS, level: '*' }
      ]
    }
  },
  
  // Employee fields
  employees: {
    first_name: { sensitivity: SENSITIVITY.INTERNAL },
    last_name: { sensitivity: SENSITIVITY.INTERNAL },
    employee_id: { sensitivity: SENSITIVITY.INTERNAL },
    email: { sensitivity: SENSITIVITY.INTERNAL },
    phone: { sensitivity: SENSITIVITY.INTERNAL },
    
    salary: {
      sensitivity: SENSITIVITY.RESTRICTED,
      allowedRoles: [
        { department: DEPARTMENTS.MANAGEMENT, level: '*' },
        { department: DEPARTMENTS.HR, level: '*' },
        { department: DEPARTMENTS.ACCOUNTS, level: [ACCESS_LEVELS.MANAGER, ACCESS_LEVELS.ADMIN] }
      ]
    },
    
    bank_details: {
      sensitivity: SENSITIVITY.RESTRICTED,
      allowedRoles: [
        { department: DEPARTMENTS.MANAGEMENT, level: [ACCESS_LEVELS.ADMIN] },
        { department: DEPARTMENTS.HR, level: [ACCESS_LEVELS.ADMIN] },
        { department: DEPARTMENTS.ACCOUNTS, level: [ACCESS_LEVELS.MANAGER, ACCESS_LEVELS.ADMIN] }
      ]
    },
    
    performance_rating: {
      sensitivity: SENSITIVITY.RESTRICTED,
      allowedRoles: [
        { department: DEPARTMENTS.MANAGEMENT, level: '*' },
        { department: DEPARTMENTS.HR, level: '*' }
      ]
    }
  },
  
  // User fields
  users: {
    username: { sensitivity: SENSITIVITY.INTERNAL },
    email: { sensitivity: SENSITIVITY.INTERNAL },
    full_name: { sensitivity: SENSITIVITY.INTERNAL },
    department: { sensitivity: SENSITIVITY.INTERNAL },
    access_level: { sensitivity: SENSITIVITY.INTERNAL },
    
    permissions: {
      sensitivity: SENSITIVITY.RESTRICTED,
      allowedRoles: [
        { department: DEPARTMENTS.MANAGEMENT, level: [ACCESS_LEVELS.ADMIN] },
        { department: DEPARTMENTS.HR, level: [ACCESS_LEVELS.ADMIN] }
      ]
    }
  },
  
  // Submittal fields (within Projects)
  submittals: {
    submittal_id: { sensitivity: SENSITIVITY.INTERNAL },
    submittal_name: { sensitivity: SENSITIVITY.INTERNAL },
    scope: { sensitivity: SENSITIVITY.INTERNAL },
    status: { sensitivity: SENSITIVITY.INTERNAL },
    
    // Sensitive planning fields
    weightage: { 
      sensitivity: SENSITIVITY.CONFIDENTIAL,
      allowedRoles: [
        { department: DEPARTMENTS.MANAGEMENT, level: '*' },
        { department: DEPARTMENTS.PROJECTS, level: [ACCESS_LEVELS.MANAGER, ACCESS_LEVELS.ADMIN] }
      ]
    },
    completion_percentage: { 
      sensitivity: SENSITIVITY.CONFIDENTIAL,
      allowedRoles: [
        { department: DEPARTMENTS.MANAGEMENT, level: '*' },
        { department: DEPARTMENTS.PROJECTS, level: [ACCESS_LEVELS.MANAGER, ACCESS_LEVELS.TEAM_LEAD, ACCESS_LEVELS.ADMIN] }
      ]
    },
    
    // Hold management
    hold_enabled: { sensitivity: SENSITIVITY.INTERNAL },
    hold_reason: { 
      sensitivity: SENSITIVITY.CONFIDENTIAL,
      allowedRoles: [
        { department: DEPARTMENTS.MANAGEMENT, level: '*' },
        { department: DEPARTMENTS.PROJECTS, level: '*' }
      ]
    },
    
    // Step details
    steps: { sensitivity: SENSITIVITY.INTERNAL }
  },

  // Lead fields
  leads: {
    company_name: { sensitivity: SENSITIVITY.PUBLIC },
    contact_person: { sensitivity: SENSITIVITY.INTERNAL },
    email: { sensitivity: SENSITIVITY.INTERNAL },
    phone: { sensitivity: SENSITIVITY.INTERNAL },
    status: { sensitivity: SENSITIVITY.INTERNAL },
    industry: { sensitivity: SENSITIVITY.INTERNAL },
    source: { sensitivity: SENSITIVITY.INTERNAL },
    notes: { 
      sensitivity: SENSITIVITY.CONFIDENTIAL,
      allowedRoles: [
        { department: DEPARTMENTS.MANAGEMENT, level: '*' },
        { department: DEPARTMENTS.SALES, level: '*' }
      ]
    }
  },

  // Lead Approval fields
  leadApprovals: {
    lead_id: { sensitivity: SENSITIVITY.INTERNAL },
    company_name: { sensitivity: SENSITIVITY.PUBLIC },
    approval_status: { sensitivity: SENSITIVITY.INTERNAL },
    reviewed_by: { sensitivity: SENSITIVITY.INTERNAL },
    rejection_reason: { 
      sensitivity: SENSITIVITY.CONFIDENTIAL,
      allowedRoles: [
        { department: DEPARTMENTS.MANAGEMENT, level: '*' }
      ]
    }
  },

  // Intern fields
  interns: {
    intern_id: { sensitivity: SENSITIVITY.PUBLIC },
    first_name: { sensitivity: SENSITIVITY.PUBLIC },
    last_name: { sensitivity: SENSITIVITY.PUBLIC },
    email: { sensitivity: SENSITIVITY.INTERNAL },
    batch_number: { sensitivity: SENSITIVITY.INTERNAL },
    department: { sensitivity: SENSITIVITY.INTERNAL },
    start_date: { sensitivity: SENSITIVITY.INTERNAL },
    end_date: { sensitivity: SENSITIVITY.INTERNAL },
    status: { sensitivity: SENSITIVITY.INTERNAL }
  },

  // Vendor fields
  vendors: {
    vendor_name: { sensitivity: SENSITIVITY.PUBLIC },
    vendor_number: { sensitivity: SENSITIVITY.PUBLIC },
    contact_person: { sensitivity: SENSITIVITY.INTERNAL },
    email: { sensitivity: SENSITIVITY.INTERNAL },
    phone: { sensitivity: SENSITIVITY.INTERNAL },
    payment_terms: { 
      sensitivity: SENSITIVITY.CONFIDENTIAL,
      allowedRoles: [
        { department: DEPARTMENTS.MANAGEMENT, level: '*' },
        { department: DEPARTMENTS.ACCOUNTS, level: '*' }
      ]
    }
  },

  // Timesheet fields
  timesheets: {
    date: { sensitivity: SENSITIVITY.INTERNAL },
    project_id: { sensitivity: SENSITIVITY.INTERNAL },
    task: { sensitivity: SENSITIVITY.INTERNAL },
    actual_hours: { sensitivity: SENSITIVITY.INTERNAL },
    earned_hours: { 
      sensitivity: SENSITIVITY.CONFIDENTIAL,
      allowedRoles: [
        { department: DEPARTMENTS.MANAGEMENT, level: '*' },
        { department: '*', level: [ACCESS_LEVELS.MANAGER, ACCESS_LEVELS.ADMIN] }
      ]
    },
    approval_status: { sensitivity: SENSITIVITY.INTERNAL },
    work_description: { sensitivity: SENSITIVITY.INTERNAL }
  },

  // Team fields
  teams: {
    team_name: { sensitivity: SENSITIVITY.PUBLIC },
    department: { sensitivity: SENSITIVITY.INTERNAL },
    team_lead: { sensitivity: SENSITIVITY.INTERNAL },
    members: { sensitivity: SENSITIVITY.INTERNAL }
  },

  // Error Tracking fields
  errorTracking: {
    error_number: { sensitivity: SENSITIVITY.INTERNAL },
    project_id: { sensitivity: SENSITIVITY.INTERNAL },
    error_description: { sensitivity: SENSITIVITY.INTERNAL },
    work_type: { sensitivity: SENSITIVITY.INTERNAL },
    status: { sensitivity: SENSITIVITY.INTERNAL },
    assigned_to: { sensitivity: SENSITIVITY.INTERNAL },
    resolution_notes: { 
      sensitivity: SENSITIVITY.CONFIDENTIAL,
      allowedRoles: [
        { department: DEPARTMENTS.MANAGEMENT, level: '*' },
        { department: DEPARTMENTS.PROJECTS, level: '*' }
      ]
    }
  },

  // Error Settings fields
  errorSettings: {
    error_code: { sensitivity: SENSITIVITY.INTERNAL },
    description: { sensitivity: SENSITIVITY.INTERNAL },
    work_type: { sensitivity: SENSITIVITY.INTERNAL },
    severity: { sensitivity: SENSITIVITY.INTERNAL }
  },

  // Webhook Settings fields
  webhooks: {
    webhook_name: { sensitivity: SENSITIVITY.INTERNAL },
    url: { 
      sensitivity: SENSITIVITY.RESTRICTED,
      allowedRoles: [
        { department: DEPARTMENTS.MANAGEMENT, level: [ACCESS_LEVELS.ADMIN] }
      ]
    },
    event_type: { sensitivity: SENSITIVITY.INTERNAL },
    is_active: { sensitivity: SENSITIVITY.INTERNAL }
  },

  // Leave Management fields
  leaveManagement: {
    application_number: { sensitivity: SENSITIVITY.INTERNAL },
    leave_type: { sensitivity: SENSITIVITY.INTERNAL },
    start_date: { sensitivity: SENSITIVITY.INTERNAL },
    end_date: { sensitivity: SENSITIVITY.INTERNAL },
    total_days: { sensitivity: SENSITIVITY.INTERNAL },
    reason: { 
      sensitivity: SENSITIVITY.CONFIDENTIAL,
      allowedRoles: [
        { department: DEPARTMENTS.MANAGEMENT, level: '*' },
        { department: '*', level: '*', condition: 'own_records' }
      ]
    },
    status: { sensitivity: SENSITIVITY.INTERNAL },
    balance_remaining: { sensitivity: SENSITIVITY.INTERNAL }
  },

  // Leave Approvals fields
  leaveApprovals: {
    application_number: { sensitivity: SENSITIVITY.INTERNAL },
    employee_name: { sensitivity: SENSITIVITY.INTERNAL },
    leave_type: { sensitivity: SENSITIVITY.INTERNAL },
    start_date: { sensitivity: SENSITIVITY.INTERNAL },
    end_date: { sensitivity: SENSITIVITY.INTERNAL },
    total_days: { sensitivity: SENSITIVITY.INTERNAL },
    reason: { sensitivity: SENSITIVITY.INTERNAL },
    approval_status: { sensitivity: SENSITIVITY.INTERNAL },
    approved_by: { sensitivity: SENSITIVITY.INTERNAL },
    rejection_reason: { 
      sensitivity: SENSITIVITY.CONFIDENTIAL,
      allowedRoles: [
        { department: DEPARTMENTS.MANAGEMENT, level: '*' },
        { department: DEPARTMENTS.HR, level: [ACCESS_LEVELS.MANAGER, ACCESS_LEVELS.ADMIN] }
      ]
    }
  },

  // HR Leave Allocations fields
  hrLeaveAllocations: {
    employee_id: { sensitivity: SENSITIVITY.INTERNAL },
    employee_name: { sensitivity: SENSITIVITY.INTERNAL },
    casual_total: { 
      sensitivity: SENSITIVITY.CONFIDENTIAL,
      allowedRoles: [
        { department: DEPARTMENTS.MANAGEMENT, level: [ACCESS_LEVELS.ADMIN] },
        { department: DEPARTMENTS.HR, level: [ACCESS_LEVELS.MANAGER, ACCESS_LEVELS.ADMIN] }
      ]
    },
    sick_total: { 
      sensitivity: SENSITIVITY.CONFIDENTIAL,
      allowedRoles: [
        { department: DEPARTMENTS.MANAGEMENT, level: [ACCESS_LEVELS.ADMIN] },
        { department: DEPARTMENTS.HR, level: [ACCESS_LEVELS.MANAGER, ACCESS_LEVELS.ADMIN] }
      ]
    },
    earned_total: { 
      sensitivity: SENSITIVITY.CONFIDENTIAL,
      allowedRoles: [
        { department: DEPARTMENTS.MANAGEMENT, level: [ACCESS_LEVELS.ADMIN] },
        { department: DEPARTMENTS.HR, level: [ACCESS_LEVELS.MANAGER, ACCESS_LEVELS.ADMIN] }
      ]
    },
    carry_forward: { 
      sensitivity: SENSITIVITY.CONFIDENTIAL,
      allowedRoles: [
        { department: DEPARTMENTS.MANAGEMENT, level: [ACCESS_LEVELS.ADMIN] },
        { department: DEPARTMENTS.HR, level: [ACCESS_LEVELS.MANAGER, ACCESS_LEVELS.ADMIN] }
      ]
    }
  },

  // Holiday Calendar fields
  hrHolidays: {
    date: { sensitivity: SENSITIVITY.PUBLIC },
    holiday_name: { sensitivity: SENSITIVITY.PUBLIC },
    type: { sensitivity: SENSITIVITY.INTERNAL },
    applicable_branches: { sensitivity: SENSITIVITY.INTERNAL }
  },

  // Performance & Awards Module
  kraSettings: {
    kra_type_name: { sensitivity: SENSITIVITY.INTERNAL },
    department: { sensitivity: SENSITIVITY.INTERNAL },
    line_items: { sensitivity: SENSITIVITY.INTERNAL },
    weightage: { sensitivity: SENSITIVITY.INTERNAL },
    is_active: { sensitivity: SENSITIVITY.INTERNAL }
  },

  employeeTargets: {
    employee_id: { sensitivity: SENSITIVITY.INTERNAL },
    employee_name: { sensitivity: SENSITIVITY.INTERNAL },
    kra_type: { sensitivity: SENSITIVITY.INTERNAL },
    targets: { sensitivity: SENSITIVITY.INTERNAL },
    effective_date: { sensitivity: SENSITIVITY.INTERNAL }
  },

  performanceSummary: {
    employee_id: { sensitivity: SENSITIVITY.INTERNAL },
    period: { sensitivity: SENSITIVITY.INTERNAL },
    achievement_percentage: { sensitivity: SENSITIVITY.INTERNAL },
    review_status: { 
      sensitivity: SENSITIVITY.CONFIDENTIAL,
      allowedRoles: [
        { department: DEPARTMENTS.MANAGEMENT, level: '*' },
        { department: DEPARTMENTS.HR, level: '*' }
      ]
    },
    review_comments: { 
      sensitivity: SENSITIVITY.CONFIDENTIAL,
      allowedRoles: [
        { department: DEPARTMENTS.MANAGEMENT, level: '*' },
        { department: DEPARTMENTS.HR, level: '*' }
      ]
    }
  },

  performanceAwards: {
    award_name: { sensitivity: SENSITIVITY.INTERNAL },
    award_type: { sensitivity: SENSITIVITY.INTERNAL },
    criteria: { sensitivity: SENSITIVITY.INTERNAL },
    bonus_value: { 
      sensitivity: SENSITIVITY.RESTRICTED,
      allowedRoles: [
        { department: DEPARTMENTS.MANAGEMENT, level: '*' },
        { department: DEPARTMENTS.HR, level: [ACCESS_LEVELS.MANAGER, ACCESS_LEVELS.ADMIN] }
      ]
    },
    nomination_status: { sensitivity: SENSITIVITY.INTERNAL }
  },

  myPerformance: {
    performance_data: { sensitivity: SENSITIVITY.INTERNAL },
    targets: { sensitivity: SENSITIVITY.INTERNAL },
    actuals: { sensitivity: SENSITIVITY.INTERNAL },
    awards: { sensitivity: SENSITIVITY.INTERNAL }
  }
};

/**
 * ACTION PERMISSIONS MATRIX
 * Define which roles can perform which actions
 */
export const ACTION_PERMISSIONS = {
  projects: {
    create: [
      { department: DEPARTMENTS.MANAGEMENT, level: '*' },
      { department: DEPARTMENTS.PROJECTS, level: [ACCESS_LEVELS.MANAGER, ACCESS_LEVELS.ADMIN] },
      { department: DEPARTMENTS.SALES, level: [ACCESS_LEVELS.MANAGER, ACCESS_LEVELS.ADMIN] }
    ],
    edit: [
      { department: DEPARTMENTS.MANAGEMENT, level: '*' },
      { department: DEPARTMENTS.PROJECTS, level: '*' }
    ],
    delete: [
      { department: DEPARTMENTS.MANAGEMENT, level: [ACCESS_LEVELS.ADMIN] },
      { department: DEPARTMENTS.PROJECTS, level: [ACCESS_LEVELS.ADMIN] }
    ],
    view: 'all'
  },
  
  submittals: {
    create: [
      { department: DEPARTMENTS.MANAGEMENT, level: '*' },
      { department: DEPARTMENTS.PROJECTS, level: [ACCESS_LEVELS.MANAGER, ACCESS_LEVELS.ADMIN] }
    ],
    edit: [
      { department: DEPARTMENTS.MANAGEMENT, level: '*' },
      { department: DEPARTMENTS.PROJECTS, level: [ACCESS_LEVELS.MANAGER, ACCESS_LEVELS.TEAM_LEAD, ACCESS_LEVELS.ADMIN] }
    ],
    delete: [
      { department: DEPARTMENTS.MANAGEMENT, level: [ACCESS_LEVELS.ADMIN] },
      { department: DEPARTMENTS.PROJECTS, level: [ACCESS_LEVELS.ADMIN] }
    ],
    view: [
      { department: DEPARTMENTS.MANAGEMENT, level: '*' },
      { department: DEPARTMENTS.PROJECTS, level: '*' },
      { department: DEPARTMENTS.ESTIMATION, level: [ACCESS_LEVELS.MANAGER, ACCESS_LEVELS.TEAM_LEAD] }
    ]
  },
  
  clients: {
    create: [
      { department: DEPARTMENTS.MANAGEMENT, level: '*' },
      { department: DEPARTMENTS.SALES, level: [ACCESS_LEVELS.MANAGER, ACCESS_LEVELS.ADMIN] }
    ],
    edit: [
      { department: DEPARTMENTS.MANAGEMENT, level: '*' },
      { department: DEPARTMENTS.SALES, level: [ACCESS_LEVELS.MANAGER, ACCESS_LEVELS.ADMIN] }
    ],
    delete: [
      { department: DEPARTMENTS.MANAGEMENT, level: [ACCESS_LEVELS.ADMIN] }
    ],
    view: 'all'
  },
  
  estimations: {
    create: [
      { department: DEPARTMENTS.MANAGEMENT, level: '*' },
      { department: DEPARTMENTS.ESTIMATION, level: '*' },
      { department: DEPARTMENTS.SALES, level: [ACCESS_LEVELS.MANAGER, ACCESS_LEVELS.ADMIN] }
    ],
    edit: [
      { department: DEPARTMENTS.MANAGEMENT, level: '*' },
      { department: DEPARTMENTS.ESTIMATION, level: '*' },
      { department: DEPARTMENTS.SALES, level: [ACCESS_LEVELS.MANAGER, ACCESS_LEVELS.ADMIN] }
    ],
    delete: [
      { department: DEPARTMENTS.MANAGEMENT, level: '*' },
      { department: DEPARTMENTS.ESTIMATION, level: [ACCESS_LEVELS.MANAGER, ACCESS_LEVELS.ADMIN] }
    ],
    view: 'all'
  },
  
  invoices: {
    create: [
      { department: DEPARTMENTS.MANAGEMENT, level: '*' },
      { department: DEPARTMENTS.ACCOUNTS, level: '*' }
    ],
    edit: [
      { department: DEPARTMENTS.MANAGEMENT, level: '*' },
      { department: DEPARTMENTS.ACCOUNTS, level: '*' }
    ],
    delete: [
      { department: DEPARTMENTS.MANAGEMENT, level: [ACCESS_LEVELS.ADMIN] },
      { department: DEPARTMENTS.ACCOUNTS, level: [ACCESS_LEVELS.ADMIN] }
    ],
    view: [
      { department: DEPARTMENTS.MANAGEMENT, level: '*' },
      { department: DEPARTMENTS.ACCOUNTS, level: '*' },
      { department: DEPARTMENTS.PROJECTS, level: [ACCESS_LEVELS.MANAGER, ACCESS_LEVELS.ADMIN] }
    ]
  },
  
  employees: {
    create: [
      { department: DEPARTMENTS.MANAGEMENT, level: '*' },
      { department: DEPARTMENTS.HR, level: '*' }
    ],
    edit: [
      { department: DEPARTMENTS.MANAGEMENT, level: '*' },
      { department: DEPARTMENTS.HR, level: '*' }
    ],
    delete: [
      { department: DEPARTMENTS.MANAGEMENT, level: [ACCESS_LEVELS.ADMIN] },
      { department: DEPARTMENTS.HR, level: [ACCESS_LEVELS.ADMIN] }
    ],
    view: [
      { department: DEPARTMENTS.MANAGEMENT, level: '*' },
      { department: DEPARTMENTS.HR, level: '*' },
      { department: '*', level: [ACCESS_LEVELS.MANAGER, ACCESS_LEVELS.ADMIN] }
    ]
  },
  
  users: {
    create: [
      { department: DEPARTMENTS.MANAGEMENT, level: [ACCESS_LEVELS.ADMIN] },
      { department: DEPARTMENTS.HR, level: [ACCESS_LEVELS.ADMIN] }
    ],
    edit: [
      { department: DEPARTMENTS.MANAGEMENT, level: [ACCESS_LEVELS.ADMIN] },
      { department: DEPARTMENTS.HR, level: [ACCESS_LEVELS.ADMIN] }
    ],
    delete: [
      { department: DEPARTMENTS.MANAGEMENT, level: [ACCESS_LEVELS.ADMIN] }
    ],
    view: [
      { department: DEPARTMENTS.MANAGEMENT, level: '*' },
      { department: DEPARTMENTS.HR, level: '*' }
    ]
  },
  
  reports: {
    view: [
      { department: DEPARTMENTS.MANAGEMENT, level: '*' },
      { department: '*', level: [ACCESS_LEVELS.MANAGER, ACCESS_LEVELS.ADMIN] }
    ],
    export: [
      { department: DEPARTMENTS.MANAGEMENT, level: '*' },
      { department: '*', level: [ACCESS_LEVELS.MANAGER, ACCESS_LEVELS.ADMIN] }
    ]
  },

  leads: {
    create: [
      { department: DEPARTMENTS.MANAGEMENT, level: '*' },
      { department: DEPARTMENTS.SALES, level: '*' }
    ],
    edit: [
      { department: DEPARTMENTS.MANAGEMENT, level: '*' },
      { department: DEPARTMENTS.SALES, level: '*' }
    ],
    delete: [
      { department: DEPARTMENTS.MANAGEMENT, level: [ACCESS_LEVELS.ADMIN] },
      { department: DEPARTMENTS.SALES, level: [ACCESS_LEVELS.MANAGER, ACCESS_LEVELS.ADMIN] }
    ],
    view: [
      { department: DEPARTMENTS.MANAGEMENT, level: '*' },
      { department: DEPARTMENTS.SALES, level: '*' }
    ],
    assign_sales: [
      { department: DEPARTMENTS.MANAGEMENT, level: '*' },
      { department: DEPARTMENTS.SALES, level: [ACCESS_LEVELS.MANAGER, ACCESS_LEVELS.ADMIN] }
    ]
  },

  leadApprovals: {
    view: [
      { department: DEPARTMENTS.MANAGEMENT, level: [ACCESS_LEVELS.MANAGER, ACCESS_LEVELS.ADMIN] }
    ],
    approve: [
      { department: DEPARTMENTS.MANAGEMENT, level: [ACCESS_LEVELS.MANAGER, ACCESS_LEVELS.ADMIN] }
    ],
    reject: [
      { department: DEPARTMENTS.MANAGEMENT, level: [ACCESS_LEVELS.MANAGER, ACCESS_LEVELS.ADMIN] }
    ]
  },

  interns: {
    create: [
      { department: DEPARTMENTS.MANAGEMENT, level: '*' },
      { department: DEPARTMENTS.HR, level: '*' }
    ],
    edit: [
      { department: DEPARTMENTS.MANAGEMENT, level: '*' },
      { department: DEPARTMENTS.HR, level: '*' }
    ],
    delete: [
      { department: DEPARTMENTS.MANAGEMENT, level: [ACCESS_LEVELS.ADMIN] },
      { department: DEPARTMENTS.HR, level: [ACCESS_LEVELS.ADMIN] }
    ],
    view: [
      { department: DEPARTMENTS.MANAGEMENT, level: '*' },
      { department: DEPARTMENTS.HR, level: '*' }
    ]
  },

  timesheets: {
    create: 'all',
    edit: [
      { department: DEPARTMENTS.MANAGEMENT, level: '*' },
      { department: '*', level: '*', condition: 'own_records' }
    ],
    delete: [
      { department: DEPARTMENTS.MANAGEMENT, level: [ACCESS_LEVELS.ADMIN] }
    ],
    view: [
      { department: DEPARTMENTS.MANAGEMENT, level: '*' },
      { department: '*', level: '*' }
    ],
    approve: [
      { department: DEPARTMENTS.MANAGEMENT, level: '*' },
      { department: '*', level: [ACCESS_LEVELS.MANAGER, ACCESS_LEVELS.ADMIN] }
    ]
  },

  vendors: {
    create: [
      { department: DEPARTMENTS.MANAGEMENT, level: '*' },
      { department: DEPARTMENTS.ACCOUNTS, level: '*' },
      { department: DEPARTMENTS.PROJECTS, level: [ACCESS_LEVELS.MANAGER, ACCESS_LEVELS.ADMIN] }
    ],
    edit: [
      { department: DEPARTMENTS.MANAGEMENT, level: '*' },
      { department: DEPARTMENTS.ACCOUNTS, level: '*' },
      { department: DEPARTMENTS.PROJECTS, level: [ACCESS_LEVELS.MANAGER, ACCESS_LEVELS.ADMIN] }
    ],
    delete: [
      { department: DEPARTMENTS.MANAGEMENT, level: [ACCESS_LEVELS.ADMIN] },
      { department: DEPARTMENTS.ACCOUNTS, level: [ACCESS_LEVELS.ADMIN] }
    ],
    view: [
      { department: DEPARTMENTS.MANAGEMENT, level: '*' },
      { department: DEPARTMENTS.ACCOUNTS, level: '*' },
      { department: DEPARTMENTS.PROJECTS, level: '*' }
    ]
  },

  errorSettings: {
    create: [
      { department: DEPARTMENTS.MANAGEMENT, level: '*' },
      { department: DEPARTMENTS.PROJECTS, level: [ACCESS_LEVELS.MANAGER, ACCESS_LEVELS.ADMIN] }
    ],
    edit: [
      { department: DEPARTMENTS.MANAGEMENT, level: '*' },
      { department: DEPARTMENTS.PROJECTS, level: [ACCESS_LEVELS.MANAGER, ACCESS_LEVELS.ADMIN] }
    ],
    delete: [
      { department: DEPARTMENTS.MANAGEMENT, level: [ACCESS_LEVELS.ADMIN] },
      { department: DEPARTMENTS.PROJECTS, level: [ACCESS_LEVELS.ADMIN] }
    ],
    view: [
      { department: DEPARTMENTS.MANAGEMENT, level: '*' },
      { department: DEPARTMENTS.PROJECTS, level: '*' }
    ]
  },

  errorTracking: {
    create: [
      { department: DEPARTMENTS.MANAGEMENT, level: '*' },
      { department: DEPARTMENTS.PROJECTS, level: '*' }
    ],
    edit: [
      { department: DEPARTMENTS.MANAGEMENT, level: '*' },
      { department: DEPARTMENTS.PROJECTS, level: '*' }
    ],
    delete: [
      { department: DEPARTMENTS.MANAGEMENT, level: [ACCESS_LEVELS.ADMIN] },
      { department: DEPARTMENTS.PROJECTS, level: [ACCESS_LEVELS.MANAGER, ACCESS_LEVELS.ADMIN] }
    ],
    view: [
      { department: DEPARTMENTS.MANAGEMENT, level: '*' },
      { department: DEPARTMENTS.PROJECTS, level: '*' }
    ]
  },

  webhooks: {
    create: [
      { department: DEPARTMENTS.MANAGEMENT, level: [ACCESS_LEVELS.ADMIN] }
    ],
    edit: [
      { department: DEPARTMENTS.MANAGEMENT, level: [ACCESS_LEVELS.ADMIN] }
    ],
    delete: [
      { department: DEPARTMENTS.MANAGEMENT, level: [ACCESS_LEVELS.ADMIN] }
    ],
    view: [
      { department: DEPARTMENTS.MANAGEMENT, level: '*' }
    ]
  },

  leaveManagement: {
    create: 'all',
    edit: [
      { department: '*', level: '*', condition: 'own_records' }
    ],
    delete: [
      { department: DEPARTMENTS.MANAGEMENT, level: [ACCESS_LEVELS.ADMIN] },
      { department: DEPARTMENTS.HR, level: [ACCESS_LEVELS.ADMIN] }
    ],
    view: 'all',
    cancel: [
      { department: '*', level: '*', condition: 'own_records' }
    ]
  },

  leaveApprovals: {
    view: [
      { department: DEPARTMENTS.MANAGEMENT, level: [ACCESS_LEVELS.MANAGER, ACCESS_LEVELS.ADMIN, ACCESS_LEVELS.TEAM_LEAD] },
      { department: DEPARTMENTS.SALES, level: [ACCESS_LEVELS.MANAGER, ACCESS_LEVELS.ADMIN, ACCESS_LEVELS.TEAM_LEAD] },
      { department: DEPARTMENTS.PROJECTS, level: [ACCESS_LEVELS.MANAGER, ACCESS_LEVELS.ADMIN, ACCESS_LEVELS.TEAM_LEAD] },
      { department: DEPARTMENTS.HR, level: [ACCESS_LEVELS.MANAGER, ACCESS_LEVELS.ADMIN] }
    ],
    approve: [
      { department: DEPARTMENTS.MANAGEMENT, level: [ACCESS_LEVELS.MANAGER, ACCESS_LEVELS.ADMIN, ACCESS_LEVELS.TEAM_LEAD] },
      { department: DEPARTMENTS.SALES, level: [ACCESS_LEVELS.MANAGER, ACCESS_LEVELS.ADMIN, ACCESS_LEVELS.TEAM_LEAD] },
      { department: DEPARTMENTS.PROJECTS, level: [ACCESS_LEVELS.MANAGER, ACCESS_LEVELS.ADMIN, ACCESS_LEVELS.TEAM_LEAD] },
      { department: DEPARTMENTS.HR, level: [ACCESS_LEVELS.MANAGER, ACCESS_LEVELS.ADMIN] }
    ],
    reject: [
      { department: DEPARTMENTS.MANAGEMENT, level: [ACCESS_LEVELS.MANAGER, ACCESS_LEVELS.ADMIN, ACCESS_LEVELS.TEAM_LEAD] },
      { department: DEPARTMENTS.SALES, level: [ACCESS_LEVELS.MANAGER, ACCESS_LEVELS.ADMIN, ACCESS_LEVELS.TEAM_LEAD] },
      { department: DEPARTMENTS.PROJECTS, level: [ACCESS_LEVELS.MANAGER, ACCESS_LEVELS.ADMIN, ACCESS_LEVELS.TEAM_LEAD] },
      { department: DEPARTMENTS.HR, level: [ACCESS_LEVELS.MANAGER, ACCESS_LEVELS.ADMIN] }
    ]
  },

  hrLeaveAllocations: {
    create: [
      { department: DEPARTMENTS.MANAGEMENT, level: [ACCESS_LEVELS.ADMIN] },
      { department: DEPARTMENTS.HR, level: [ACCESS_LEVELS.MANAGER, ACCESS_LEVELS.ADMIN] }
    ],
    edit: [
      { department: DEPARTMENTS.MANAGEMENT, level: [ACCESS_LEVELS.ADMIN] },
      { department: DEPARTMENTS.HR, level: [ACCESS_LEVELS.MANAGER, ACCESS_LEVELS.ADMIN] }
    ],
    delete: [
      { department: DEPARTMENTS.MANAGEMENT, level: [ACCESS_LEVELS.ADMIN] },
      { department: DEPARTMENTS.HR, level: [ACCESS_LEVELS.ADMIN] }
    ],
    view: [
      { department: DEPARTMENTS.MANAGEMENT, level: [ACCESS_LEVELS.ADMIN] },
      { department: DEPARTMENTS.HR, level: [ACCESS_LEVELS.MANAGER, ACCESS_LEVELS.ADMIN] }
    ]
  },

  hrHolidays: {
    create: [
      { department: DEPARTMENTS.MANAGEMENT, level: [ACCESS_LEVELS.ADMIN] },
      { department: DEPARTMENTS.HR, level: [ACCESS_LEVELS.MANAGER, ACCESS_LEVELS.ADMIN] }
    ],
    edit: [
      { department: DEPARTMENTS.MANAGEMENT, level: [ACCESS_LEVELS.ADMIN] },
      { department: DEPARTMENTS.HR, level: [ACCESS_LEVELS.MANAGER, ACCESS_LEVELS.ADMIN] }
    ],
    delete: [
      { department: DEPARTMENTS.MANAGEMENT, level: [ACCESS_LEVELS.ADMIN] },
      { department: DEPARTMENTS.HR, level: [ACCESS_LEVELS.MANAGER, ACCESS_LEVELS.ADMIN] }
    ],
    view: [
      { department: DEPARTMENTS.MANAGEMENT, level: [ACCESS_LEVELS.ADMIN] },
      { department: DEPARTMENTS.HR, level: [ACCESS_LEVELS.MANAGER, ACCESS_LEVELS.ADMIN] }
    ]
  },

  wbsMasterList: {
    create: [
      { department: DEPARTMENTS.MANAGEMENT, level: [ACCESS_LEVELS.ADMIN, ACCESS_LEVELS.MANAGER] },
      { department: DEPARTMENTS.ESTIMATION, level: [ACCESS_LEVELS.ADMIN, ACCESS_LEVELS.MANAGER] }
    ],
    edit: [
      { department: DEPARTMENTS.MANAGEMENT, level: [ACCESS_LEVELS.ADMIN, ACCESS_LEVELS.MANAGER] },
      { department: DEPARTMENTS.ESTIMATION, level: [ACCESS_LEVELS.ADMIN, ACCESS_LEVELS.MANAGER] }
    ],
    delete: [
      { department: DEPARTMENTS.MANAGEMENT, level: [ACCESS_LEVELS.ADMIN, ACCESS_LEVELS.MANAGER] },
      { department: DEPARTMENTS.ESTIMATION, level: [ACCESS_LEVELS.ADMIN, ACCESS_LEVELS.MANAGER] }
    ],
    view: [
      { department: DEPARTMENTS.MANAGEMENT, level: [ACCESS_LEVELS.ADMIN, ACCESS_LEVELS.MANAGER] },
      { department: DEPARTMENTS.ESTIMATION, level: [ACCESS_LEVELS.ADMIN, ACCESS_LEVELS.MANAGER] }
    ]
  },

  wbsEstimations: {
    create: [
      { department: DEPARTMENTS.MANAGEMENT, level: [ACCESS_LEVELS.ADMIN, ACCESS_LEVELS.MANAGER, ACCESS_LEVELS.TEAM_LEAD] },
      { department: DEPARTMENTS.ESTIMATION, level: "*" }
    ],
    edit: [
      { department: DEPARTMENTS.MANAGEMENT, level: [ACCESS_LEVELS.ADMIN, ACCESS_LEVELS.MANAGER, ACCESS_LEVELS.TEAM_LEAD] },
      { department: DEPARTMENTS.ESTIMATION, level: "*" }
    ],
    delete: [
      { department: DEPARTMENTS.MANAGEMENT, level: [ACCESS_LEVELS.ADMIN, ACCESS_LEVELS.MANAGER] },
      { department: DEPARTMENTS.ESTIMATION, level: [ACCESS_LEVELS.ADMIN, ACCESS_LEVELS.MANAGER] }
    ],
    view: [
      { department: DEPARTMENTS.MANAGEMENT, level: [ACCESS_LEVELS.ADMIN, ACCESS_LEVELS.MANAGER, ACCESS_LEVELS.TEAM_LEAD] },
      { department: DEPARTMENTS.ESTIMATION, level: "*" }
    ],
    export: [
      { department: DEPARTMENTS.MANAGEMENT, level: [ACCESS_LEVELS.ADMIN, ACCESS_LEVELS.MANAGER] },
      { department: DEPARTMENTS.ESTIMATION, level: [ACCESS_LEVELS.ADMIN, ACCESS_LEVELS.MANAGER, ACCESS_LEVELS.TEAM_LEAD] }
    ]
  },

  // Performance & Awards Module
  kraSettings: {
    create: [
      { department: DEPARTMENTS.MANAGEMENT, level: '*' },
      { department: DEPARTMENTS.HR, level: [ACCESS_LEVELS.MANAGER, ACCESS_LEVELS.ADMIN] }
    ],
    edit: [
      { department: DEPARTMENTS.MANAGEMENT, level: '*' },
      { department: DEPARTMENTS.HR, level: [ACCESS_LEVELS.MANAGER, ACCESS_LEVELS.ADMIN] }
    ],
    delete: [
      { department: DEPARTMENTS.MANAGEMENT, level: [ACCESS_LEVELS.ADMIN] },
      { department: DEPARTMENTS.HR, level: [ACCESS_LEVELS.ADMIN] }
    ],
    view: [
      { department: DEPARTMENTS.MANAGEMENT, level: '*' },
      { department: DEPARTMENTS.HR, level: [ACCESS_LEVELS.MANAGER, ACCESS_LEVELS.ADMIN] }
    ]
  },

  employeeTargets: {
    create: [
      { department: DEPARTMENTS.MANAGEMENT, level: '*' },
      { department: DEPARTMENTS.HR, level: [ACCESS_LEVELS.MANAGER, ACCESS_LEVELS.ADMIN] }
    ],
    edit: [
      { department: DEPARTMENTS.MANAGEMENT, level: '*' },
      { department: DEPARTMENTS.HR, level: [ACCESS_LEVELS.MANAGER, ACCESS_LEVELS.ADMIN] }
    ],
    delete: [
      { department: DEPARTMENTS.MANAGEMENT, level: [ACCESS_LEVELS.ADMIN] }
    ],
    view: [
      { department: DEPARTMENTS.MANAGEMENT, level: '*' },
      { department: DEPARTMENTS.HR, level: [ACCESS_LEVELS.MANAGER, ACCESS_LEVELS.ADMIN] }
    ],
    export: [
      { department: DEPARTMENTS.MANAGEMENT, level: '*' },
      { department: DEPARTMENTS.HR, level: [ACCESS_LEVELS.MANAGER, ACCESS_LEVELS.ADMIN] }
    ]
  },

  performanceSummary: {
    create: [
      { department: DEPARTMENTS.MANAGEMENT, level: '*' },
      { department: DEPARTMENTS.HR, level: '*' }
    ],
    edit: [
      { department: DEPARTMENTS.MANAGEMENT, level: '*' },
      { department: DEPARTMENTS.HR, level: '*' }
    ],
    delete: [
      { department: DEPARTMENTS.MANAGEMENT, level: [ACCESS_LEVELS.ADMIN] }
    ],
    view: [
      { department: DEPARTMENTS.MANAGEMENT, level: '*' },
      { department: DEPARTMENTS.HR, level: '*' },
      { department: '*', level: [ACCESS_LEVELS.MANAGER, ACCESS_LEVELS.ADMIN] }
    ],
    export: [
      { department: DEPARTMENTS.MANAGEMENT, level: '*' },
      { department: DEPARTMENTS.HR, level: '*' }
    ],
    approve: [
      { department: DEPARTMENTS.MANAGEMENT, level: '*' },
      { department: DEPARTMENTS.HR, level: [ACCESS_LEVELS.MANAGER, ACCESS_LEVELS.ADMIN] }
    ]
  },

  performanceAwards: {
    create: [
      { department: DEPARTMENTS.MANAGEMENT, level: '*' },
      { department: DEPARTMENTS.HR, level: '*' }
    ],
    edit: [
      { department: DEPARTMENTS.MANAGEMENT, level: '*' },
      { department: DEPARTMENTS.HR, level: '*' }
    ],
    delete: [
      { department: DEPARTMENTS.MANAGEMENT, level: [ACCESS_LEVELS.ADMIN] },
      { department: DEPARTMENTS.HR, level: [ACCESS_LEVELS.ADMIN] }
    ],
    view: [
      { department: DEPARTMENTS.MANAGEMENT, level: '*' },
      { department: DEPARTMENTS.HR, level: '*' },
      { department: '*', level: [ACCESS_LEVELS.MANAGER, ACCESS_LEVELS.ADMIN, ACCESS_LEVELS.TEAM_LEAD] }
    ],
    nominate: [
      { department: DEPARTMENTS.MANAGEMENT, level: '*' },
      { department: DEPARTMENTS.HR, level: '*' },
      { department: '*', level: [ACCESS_LEVELS.MANAGER, ACCESS_LEVELS.ADMIN, ACCESS_LEVELS.TEAM_LEAD] }
    ],
    approve: [
      { department: DEPARTMENTS.MANAGEMENT, level: '*' },
      { department: DEPARTMENTS.HR, level: [ACCESS_LEVELS.MANAGER, ACCESS_LEVELS.ADMIN] }
    ]
  },

  performanceReports: {
    create: [
      { department: DEPARTMENTS.MANAGEMENT, level: '*' },
      { department: DEPARTMENTS.HR, level: [ACCESS_LEVELS.MANAGER, ACCESS_LEVELS.ADMIN] }
    ],
    view: [
      { department: DEPARTMENTS.MANAGEMENT, level: '*' },
      { department: DEPARTMENTS.HR, level: [ACCESS_LEVELS.MANAGER, ACCESS_LEVELS.ADMIN] }
    ],
    export: [
      { department: DEPARTMENTS.MANAGEMENT, level: '*' },
      { department: DEPARTMENTS.HR, level: [ACCESS_LEVELS.MANAGER, ACCESS_LEVELS.ADMIN] }
    ]
  },

  myPerformance: {
    view: 'all',
    export: 'all'
  }
};

/**
 * SPECIAL RULES
 * Complex business logic that doesn't fit in simple matrices
 */
export const SPECIAL_RULES = {
  // Sales can see their own or team data
  salesDataScope: {
    [ACCESS_LEVELS.TEAM_MEMBER]: 'assigned_only',
    [ACCESS_LEVELS.TEAM_LEAD]: 'team_with_toggle',
    [ACCESS_LEVELS.MANAGER]: 'all',
    [ACCESS_LEVELS.ADMIN]: 'all'
  },
  
  // Project managers can see projects they manage
  projectDataScope: {
    [ACCESS_LEVELS.TEAM_MEMBER]: 'assigned_only',
    [ACCESS_LEVELS.TEAM_LEAD]: 'team_projects',
    [ACCESS_LEVELS.MANAGER]: 'all',
    [ACCESS_LEVELS.ADMIN]: 'all'
  },
  
  // Cost visibility rules
  costVisibility: {
    always_visible: [
      { department: DEPARTMENTS.MANAGEMENT, level: '*' },
      { department: DEPARTMENTS.ACCOUNTS, level: '*' }
    ],
    conditional_visible: [
      { department: DEPARTMENTS.SALES, level: '*', condition: 'assigned_records' },
      { department: DEPARTMENTS.PROJECTS, level: [ACCESS_LEVELS.MANAGER, ACCESS_LEVELS.ADMIN] }
    ]
  },
  
  // Approval workflows
  approvalAuthority: {
    timesheets: [
      { department: DEPARTMENTS.MANAGEMENT, level: '*' },
      { department: '*', level: [ACCESS_LEVELS.MANAGER, ACCESS_LEVELS.ADMIN] }
    ],
    change_orders: [
      { department: DEPARTMENTS.MANAGEMENT, level: '*' },
      { department: DEPARTMENTS.PROJECTS, level: [ACCESS_LEVELS.MANAGER, ACCESS_LEVELS.ADMIN] }
    ],
    invoices: [
      { department: DEPARTMENTS.MANAGEMENT, level: '*' },
      { department: DEPARTMENTS.ACCOUNTS, level: [ACCESS_LEVELS.MANAGER, ACCESS_LEVELS.ADMIN] }
    ]
  }
};

export default {
  PAGE_ACCESS,
  FIELD_PERMISSIONS,
  ACTION_PERMISSIONS,
  SPECIAL_RULES,
  DEPARTMENTS,
  ACCESS_LEVELS,
  SENSITIVITY
};
